/**
 * Icono SVG inline para representar el tipo de archivo adjunto.
 * Detecta por mime_type o por extensión del nombre como fallback.
 * Variantes: PDF / DOC / XLS / IMG / generic.
 *
 * Uso típico: <FileIcon mime={a.mime_type} name={a.nombre_original} />
 * El componente acepta `size` (px) y aplica color por tipo.
 */
type Variant = 'pdf' | 'doc' | 'xls' | 'img' | 'generic';

function detectVariant(mime?: string | null, name?: string | null): Variant {
  const m = (mime ?? '').toLowerCase();
  const n = (name ?? '').toLowerCase();
  if (m.includes('pdf') || n.endsWith('.pdf')) return 'pdf';
  if (m.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg|tiff?)$/.test(n)) return 'img';
  if (m.includes('spreadsheet') || m.includes('excel') || /\.(xlsx?|csv|ods)$/.test(n)) return 'xls';
  if (m.includes('word') || m.includes('document') || /\.(docx?|odt|rtf)$/.test(n)) return 'doc';
  return 'generic';
}

const COLORS: Record<Variant, { bg: string; text: string; label: string }> = {
  pdf:     { bg: '#fef2f2', text: '#dc2626', label: 'PDF' },
  doc:     { bg: '#eff6ff', text: '#2563eb', label: 'DOC' },
  xls:     { bg: '#ecfdf5', text: '#059669', label: 'XLS' },
  img:     { bg: '#fef3c7', text: '#d97706', label: 'IMG' },
  generic: { bg: '#f1f5f9', text: '#475569', label: 'FILE' },
};

export function FileIcon({
  mime,
  name,
  size = 36,
  className,
}: {
  mime?: string | null;
  name?: string | null;
  size?: number;
  className?: string;
}) {
  const v = detectVariant(mime, name);
  const c = COLORS[v];
  // SVG inline: hoja con esquina doblada + etiqueta del tipo
  return (
    <svg
      width={size}
      height={Math.round(size * 1.25)}
      viewBox="0 0 36 45"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label={`Archivo ${c.label}`}
      role="img"
    >
      <path
        d="M3 0 H24 L33 9 V42 A3 3 0 0 1 30 45 H3 A3 3 0 0 1 0 42 V3 A3 3 0 0 1 3 0 Z"
        fill={c.bg}
        stroke={c.text}
        strokeOpacity="0.25"
        strokeWidth="1"
      />
      <path
        d="M24 0 V6 A3 3 0 0 0 27 9 H33"
        fill="none"
        stroke={c.text}
        strokeOpacity="0.5"
        strokeWidth="1"
      />
      <rect x="3" y="22" width="30" height="13" rx="2" fill={c.text} />
      <text
        x="18"
        y="32"
        textAnchor="middle"
        fontSize="9"
        fontWeight="bold"
        fontFamily="sans-serif"
        fill="white"
        letterSpacing="0.5"
      >
        {c.label}
      </text>
    </svg>
  );
}
